import React, {Component, createContext} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Dimensions} from 'react-native';
import {getAccessToken} from './storage';
import ErrorReportingService from './ErrorReportingService';
import Observables from "@happy-gastro/global-observables";
import {
    checkBookingsUpdated,
    checkSystemDataUpdated,
    triggerBookingsFetch,
    triggerSystemDataFetch
} from './BackgroundTasks';
import {defaultPosGridColumns, isLegacyPhoneColumnDefault} from './posLayout';
import {shouldSkipSystemDataFetch} from './systemDataThrottle';
import {
    applyPairingMap,
    loadPrinterPairings,
    loadPrinterSettings,
    savePrinterPairing,
    savePrinterSettings,
} from './securePrinterStorage';
import i18n from '../../i18n';

export const SystemContext = createContext();

class SystemProvider extends Component {
    state = {
        foods: [],
        categories: [],
        events: [],
        restaurant: null,
        restaurantPlace: null,
        loaded: false,
        defaultView: 'categories',
        eventColumnCount: 2,
        categoryColumnCount: 2,
        productDisplayMode: 'list', // 'list' or 'grid'
        productColumnCount: 2, // Default number of columns for product grid view
        foodSortMode: 'order', // 'order' (manual menu order) or 'mostSold'
        selectedEvent: null,
        selectedCategory: null,
        token: null,
        vipCards: [],
        bookings: [],
        admins: [],
        printerEvents: [],
        printers: [],
        todoList: []
    };

    // In-memory cache of the durable pairing map (see securePrinterStorage). Kept
    // as an instance field (not state) so setPrinterEvents can overlay pairings
    // synchronously without an extra render, mirroring the storage-layer overlay.
    _pairingMap = {};

    persistPrinterSettings = async (printers = this.state.printers, printerEvents = this.state.printerEvents) => {
        try {
            await savePrinterSettings({ printers, printerEvents });
        } catch (e) {
            console.warn('[SystemContext] Failed to persist secure printer settings:', e?.message);
        }
    };

    safeJsonParse = (value, fallback = null) => {
        if (!value) return fallback;
        try {
            return JSON.parse(value);
        } catch (error) {
            ErrorReportingService.error('Failed to parse cached system data', { error, value }, 'SystemContext');
            return fallback;
        }
    };

    // Set default column counts based on screen width
    setDefaultColumnCounts = () => {
        const { width } = Dimensions.get('window');
        const defaultCount = defaultPosGridColumns(width);
        const categoryCount = defaultCount;
        const productCount = defaultCount;
        const eventCount = defaultCount;

        this.setState({ 
            categoryColumnCount: categoryCount,
            productColumnCount: productCount,
            eventColumnCount: eventCount
        });

        AsyncStorage.setItem('categoryColumnCount', String(categoryCount));
        AsyncStorage.setItem('productColumnCount', String(productCount));
        AsyncStorage.setItem('eventColumnCount', String(eventCount));
    };

    fetchStoredDatas = async () => {
        // One AsyncStorage.multiGet (single SQLite query / bridge round-trip)
        // instead of 9 sequential awaited getItem calls on the boot provider
        // mount path. Semantics are identical — multiGet returns [[key,value],…]
        // with value === string|null, exactly like getItem.
        const stored = Object.fromEntries(await AsyncStorage.multiGet([
            'foods', 'categories', 'events', 'vipCards', 'bookings',
            'admins', 'printers', 'printerEvents', 'todoList',
        ]));
        const storedFoods = stored.foods;
        const storedCategories = stored.categories;
        const storedEvents = stored.events;
        const storedVipCards = stored.vipCards;
        const storedBookings = stored.bookings;
        const storedAdmins = stored.admins;
        const storedPrinters = stored.printers;
        const storedPrinterEvents = stored.printerEvents;
        const storedTodoList = stored.todoList;

        if (storedTodoList) {
            this.setState({ todoList: JSON.parse(storedTodoList) });
        } else {
            const defaultTodoList = [
                { description: i18n.t('todo_default_open_restaurant'), time: "10:00", completed: false },
                { description: i18n.t('todo_default_check_stock'), time: "10:00", completed: false },
                { description: i18n.t('todo_default_clean_kitchen'), time: "10:00", completed: false },
                { description: i18n.t('todo_default_lunch_service'), time: "12:00", completed: false },
                { description: i18n.t('todo_default_restock'), time: "14:00", completed: false },
                { description: i18n.t('todo_default_dinner_service'), time: "18:00", completed: false },
                { description: i18n.t('todo_default_close_restaurant'), time: "22:00" , completed: false}
            ];
            this.setState({ todoList: defaultTodoList });
            await AsyncStorage.setItem('todoList', JSON.stringify(defaultTodoList));
        }
        const cachedState = {};

        if (storedFoods) cachedState.foods = this.safeJsonParse(storedFoods, []);
        if (storedCategories) cachedState.categories = this.safeJsonParse(storedCategories, []);
        if (storedEvents) cachedState.events = this.safeJsonParse(storedEvents, []);
        if (storedVipCards) cachedState.vipCards = this.safeJsonParse(storedVipCards, []);
        if (storedBookings) cachedState.bookings = this.safeJsonParse(storedBookings, []);
        if (storedAdmins) cachedState.admins = this.safeJsonParse(storedAdmins, []);
        if (storedPrinters) cachedState.printers = this.safeJsonParse(storedPrinters, []);
        if (storedPrinterEvents) cachedState.printerEvents = this.safeJsonParse(storedPrinterEvents, []);

        if (Object.keys(cachedState).length > 0) {
            this.setState(cachedState);
        }

        return {
            foods: cachedState.foods || this.state.foods,
            categories: cachedState.categories || this.state.categories,
            events: cachedState.events || this.state.events,
            vipCards: cachedState.vipCards || this.state.vipCards,
            bookings: cachedState.bookings || this.state.bookings,
            admins: cachedState.admins || this.state.admins,
        };
    }

      _fetchBookingsFromApi = async()=> {
        try {
            // First check if we have stored bookings
            const storedBookings = await AsyncStorage.getItem('bookings');
            if (storedBookings) {
                const bookingsData = JSON.parse(storedBookings);
                this.setState({ bookings: bookingsData });
            }

            // Trigger background task to fetch fresh bookings
            await triggerBookingsFetch();

            // Check if bookings have been updated in background
            const isUpdated = await checkBookingsUpdated();

            if (isUpdated) {
                // If bookings were updated, fetch the new data from AsyncStorage
                const bookingsData = await AsyncStorage.getItem('bookings');
                if (bookingsData) {
                    const parsedBookings = JSON.parse(bookingsData);
                    this.setState({ bookings: parsedBookings });
                    return parsedBookings;
                }
            }

            return this.state.bookings;
        } catch (error) {
            ErrorReportingService.error("Failed to fetch bookings", { error }, "SystemContext");
            return this.state.bookings;
        }
    }

    /**
     * Refresh system data (catalog, VIP cards, bookings, admins).
     *
     * Concurrent callers share one in-flight run, and automatic refreshes
     * within SYSTEM_DATA_MIN_INTERVAL_MS of the last completed run for the same
     * place are skipped (see systemDataThrottle.js). Explicit refreshes pass
     * `{ force: true }`; a place switch is detected via the stored place id.
     *
     * @param {{force?: boolean}} [options]
     * @returns {Promise<void>}
     */
    fetchSystemData = async (options) => {
        const force = options?.force === true;

        if (this._systemDataInFlight) {
            return this._systemDataInFlight;
        }

        const scopeKey = await this._getSystemDataScopeKey();

        if (this._systemDataInFlight) {
            return this._systemDataInFlight;
        }

        if (shouldSkipSystemDataFetch({
            force,
            lastFetchAt: this._lastSystemDataFetchAt ?? null,
            lastScopeKey: this._lastSystemDataScopeKey ?? null,
            scopeKey,
            now: Date.now(),
        })) {
            Observables.call('systemDataLoaded');
            return undefined;
        }

        const run = this._runSystemDataFetch();
        this._systemDataInFlight = run;

        try {
            return await run;
        } finally {
            this._systemDataInFlight = null;
            this._lastSystemDataFetchAt = Date.now();
            this._lastSystemDataScopeKey = scopeKey;
        }
    };

    _getSystemDataScopeKey = async () => {
        try {
            const place = this.safeJsonParse(await AsyncStorage.getItem('restaurantPlace'), null);

            return place?._id ? String(place._id) : null;
        } catch (_error) {
            return null;
        }
    };

    _runSystemDataFetch = async () => {
        const token = await getAccessToken();
        // Single multiGet for the 8 independent settings keys (one bridge
        // round-trip instead of 8 sequential awaits) on the boot mount path.
        const [
            restaurantData,
            restaurantPlaceData,
            storedDefaultView,
            storedEventColumnCount,
            storedCategoryColumnCount,
            storedProductDisplayMode,
            storedProductColumnCount,
            storedFoodSortMode,
            phoneGridDefaultsMigrated,
        ] = (await AsyncStorage.multiGet([
            'restaurant',
            'restaurantPlace',
            'defaultView',
            'eventColumnCount',
            'categoryColumnCount',
            'productDisplayMode',
            'productColumnCount',
            'foodSortMode',
            'posPhoneGridDefaultsV2',
        ])).map(([, value]) => value);

        if (token && token !== this.state.token) {
            this.setState({ token });
        }

        // One-time phone migration: the old phone default was a single catalog
        // column, rendered as tall half-width tiles. Bump exactly that legacy
        // value to 2 columns once; anything else was picked by the user and is
        // kept, and a later deliberate "1" survives because the flag is set.
        let eventColumns = storedEventColumnCount;
        let categoryColumns = storedCategoryColumnCount;
        let productColumns = storedProductColumnCount;

        if (!phoneGridDefaultsMigrated) {
            const { width } = Dimensions.get('window');
            const migrated = [];

            if (isLegacyPhoneColumnDefault(eventColumns, width)) {
                eventColumns = '2';
                migrated.push(['eventColumnCount', '2']);
            }
            if (isLegacyPhoneColumnDefault(categoryColumns, width)) {
                categoryColumns = '2';
                migrated.push(['categoryColumnCount', '2']);
            }
            if (isLegacyPhoneColumnDefault(productColumns, width)) {
                productColumns = '2';
                migrated.push(['productColumnCount', '2']);
            }
            AsyncStorage.multiSet([...migrated, ['posPhoneGridDefaultsV2', '1']]).catch(() => {});
        }

        // Set display mode settings if they exist
        if (storedDefaultView) this.setState({ defaultView: storedDefaultView });
        if (eventColumns) this.setState({ eventColumnCount: Number(eventColumns) });
        if (categoryColumns) this.setState({ categoryColumnCount: Number(categoryColumns) });
        if (storedProductDisplayMode) this.setState({ productDisplayMode: storedProductDisplayMode });
        if (productColumns) this.setState({ productColumnCount: Number(productColumns) });
        if (storedFoodSortMode) this.setState({ foodSortMode: storedFoodSortMode });

        // First load data from AsyncStorage to show something immediately
        const cachedData = await this.fetchStoredDatas();

        const hasCachedSystemData =
            (cachedData.foods?.length || 0) > 0 ||
            (cachedData.categories?.length || 0) > 0 ||
            (cachedData.events?.length || 0) > 0;

        // Set initial state with stored data (but don't set loaded to true yet)
        this.setState({
            restaurant: this.safeJsonParse(restaurantData, null),
            restaurantPlace: this.safeJsonParse(restaurantPlaceData, null),
            loaded: hasCachedSystemData
        });

        if (!token || !restaurantData || !restaurantPlaceData) {
            Observables.call('systemDataLoaded');
            return;
        }

        try {
            // Trigger background task to fetch fresh data
            const fetchSucceeded = await triggerSystemDataFetch();

            // Check if data has been updated in background
            const isUpdated = await checkSystemDataUpdated();

            if (fetchSucceeded && isUpdated) {
                // If data was updated, fetch the new data from AsyncStorage
                const foodsData = await AsyncStorage.getItem('foods');
                const categoriesData = await AsyncStorage.getItem('categories');
                const vipCardsData = await AsyncStorage.getItem('vipCards');
                const bookingsData = await AsyncStorage.getItem('bookings');
                const adminsData = await AsyncStorage.getItem('admins');

                const nextFoods = this.safeJsonParse(foodsData, []);
                const nextCategories = this.safeJsonParse(categoriesData, []);

                // Update state with fresh data and set loaded to true.
                // Never let a background refresh replace a populated catalog with
                // an empty one — a transient failure (expired token, network blip,
                // mis-shaped payload) can adopt [] and blank out the POS grid the
                // user is already looking at. This mirrors the printers empty-guard
                // in componentDidUpdate below and pairs with persistCatalogIfHealthy
                // in BackgroundTasks (defence in depth).
                this.setState((prevState) => ({
                    foods: (Array.isArray(nextFoods) && nextFoods.length === 0 && (prevState.foods?.length || 0) > 0)
                        ? prevState.foods
                        : nextFoods,
                    categories: (Array.isArray(nextCategories) && nextCategories.length === 0 && (prevState.categories?.length || 0) > 0)
                        ? prevState.categories
                        : nextCategories,
                    vipCards: this.safeJsonParse(vipCardsData, []),
                    bookings: this.safeJsonParse(bookingsData, []),
                    admins: this.safeJsonParse(adminsData, []),
                    loaded: true
                }));
            } else {
                // If no update, still set loaded to true
                this.setState({ loaded: true });
            }
        } catch (error) {
            ErrorReportingService.error("Failed to fetch system data", { error }, "SystemContext");
            // We already loaded data from storage, so the app can still function
            // Set loaded to true even if there was an error
            this.setState({ loaded: true });
        } finally {
            Observables.call('systemDataLoaded');
        }
    };

    addTodo = async (todo) => {
        const updatedTodoList = [...this.state.todoList, todo];
        this.setState({ todoList: updatedTodoList });
        await AsyncStorage.setItem('todoList', JSON.stringify(updatedTodoList));
    };

    async componentDidMount() {
        const token = await getAccessToken();
        let parsedPrinters = [];
        let parsedPrinterEvents = [];

        try {
            // loadPrinterSettings already overlays pairings; also cache the raw
            // map so setPrinterEvents can re-apply it to future profile updates.
            const [persisted, pairingMap] = await Promise.all([
                loadPrinterSettings(),
                loadPrinterPairings(),
            ]);
            parsedPrinters = persisted.printers || [];
            parsedPrinterEvents = persisted.printerEvents || [];
            this._pairingMap = pairingMap || {};
        } catch (e) {
            console.warn('[SystemContext] Failed to load secure printer settings:', e?.message);
        }

        // CRITICAL: On cold start, if SecureStore returned an empty list but
        // redux-persist has rehydrated printers into the Redux store (legacy
        // storage path), prefer the Redux copy and migrate it back into
        // SecureStore so we never silently wipe the user's configuration.
        try {
            // Lazy require to avoid circular deps with store bootstrap.
            const { store } = require('../store');
            const reduxState = store.getState?.();
            const reduxPrinters = reduxState?.device?.printers || [];
            const reduxPrinterEvents = reduxState?.device?.printerEvents || [];

            if (parsedPrinters.length === 0 && reduxPrinters.length > 0) {
                parsedPrinters = reduxPrinters;
            }
            if (parsedPrinterEvents.length === 0 && reduxPrinterEvents.length > 0) {
                parsedPrinterEvents = reduxPrinterEvents;
            }

            // Heal SecureStore if it was empty but Redux had data.
            if ((reduxPrinters.length > 0 || reduxPrinterEvents.length > 0)) {
                this.persistPrinterSettings(parsedPrinters, parsedPrinterEvents);
            }
        } catch (_) {
            // If the store isn't ready yet, we fall through with whatever we loaded.
        }

        this.setState({
            token: token,
            printers: parsedPrinters,
            printerEvents: parsedPrinterEvents,
        });

        // Check if column counts are already set in AsyncStorage
        const storedCategoryColumnCount = await AsyncStorage.getItem('categoryColumnCount');
        const storedProductColumnCount = await AsyncStorage.getItem('productColumnCount');
        const storedEventColumnCount = await AsyncStorage.getItem('eventColumnCount');

        // If any column count is not set, set default column counts based on screen size
        if (!storedCategoryColumnCount || !storedProductColumnCount || !storedEventColumnCount) {
            this.setDefaultColumnCounts();
        }

        // Note: Background tasks are now registered in App.jsx to avoid duplicate registration
        // We only need to trigger the fetch here

        Observables.register('fetchSystemData', this.fetchSystemData);
        this.fetchSystemData();
    }

    componentWillUnmount() {
        // Drop the observer so a full app restart (root remount via `restartApp`)
        // can re-register `fetchSystemData` bound to the fresh instance instead
        // of leaving a stale callback tied to this unmounted one.
        if (typeof Observables.remove === 'function') {
            Observables.remove('fetchSystemData');
        }
    }


    componentDidUpdate(prevProps, prevState) {
        if (prevState.printerEvents !== this.state.printerEvents) {
            // IMPORTANT: Never overwrite stored printer settings with an empty
            // array — this protects against race conditions during startup where
            // the initial [] state could wipe out persisted settings.
            if (this.state.printerEvents && this.state.printerEvents.length > 0) {
                this.persistPrinterSettings(this.state.printers, this.state.printerEvents);
            } else if (prevState.printerEvents && prevState.printerEvents.length > 0) {
                // Previous state had data, new state is empty — this is likely
                // a bug/race condition. Restore from the previous state.
                console.warn('[SystemContext] Prevented printerEvents from being cleared – restoring previous value');
                this.setState({ printerEvents: prevState.printerEvents });
            }
        }

        if (prevState.printers !== this.state.printers) {
            if (this.state.printers && this.state.printers.length > 0) {
                this.persistPrinterSettings(this.state.printers, this.state.printerEvents);
            } else if (prevState.printers && prevState.printers.length > 0) {
                console.warn('[SystemContext] Prevented printers from being cleared – restoring previous value');
                this.setState({ printers: prevState.printers });
            }
        }
    }

    /**
     * Stable context value: rebuilt only when `this.state` changes, with action
     * functions created once. Previously every render handed consumers a new
     * object and brand-new setter identities, re-firing their effects/callbacks.
     *
     * @returns {object}
     */
    _getContextValue = () => {
        if (!this._contextActions) {
            this._contextActions = {
                fetchSystemData: this.fetchSystemData,
                fetchBookingsFromApi: this._fetchBookingsFromApi,
                addTodo: this.addTodo,
                setSelectedCategory: category => this.setState({ selectedCategory: category }),
                setSelectedEvent: event => this.setState({ selectedEvent: event }),
                setPrinters: (p) => {
                    this.setState({printers: p}, () => {
                        this.persistPrinterSettings(this.state.printers, this.state.printerEvents);
                    });
                },
                setPrinterEvents: (pE) => {
                    // Overlay durable pairings so a fresh/refetched profile array
                    // (which may have dropped `event.printers`) never loses the
                    // device binding. The storage layer re-applies them too.
                    const overlaid = applyPairingMap(pE, this._pairingMap);
                    this.setState({ printerEvents: overlaid }, () => {
                        this.persistPrinterSettings(this.state.printers, this.state.printerEvents);
                    });
                },
                setPrinterPairing: (event, printers = []) => {
                    // Authoritative pair/unpair for a single profile: update the
                    // in-memory map + state, then persist via the dedicated path
                    // (the only one allowed to clear a pairing).
                    const key = event?._id || event?.id;
                    this._pairingMap = {
                        ...this._pairingMap,
                        [String(key || `tn:${event?.type}::${event?.name}`)]: {
                            printers: Array.isArray(printers) ? printers : [],
                            type: event?.type,
                            name: event?.name,
                        },
                    };
                    this.setState((prev) => ({
                        printerEvents: (prev.printerEvents || []).map((e) =>
                            (e._id || e.id) === key ? { ...e, printers } : e,
                        ),
                    }), () => {
                        savePrinterPairing(event, printers).catch((e) =>
                            console.warn('[SystemContext] savePrinterPairing failed', e?.message));
                    });
                },
                setProductDisplayMode: (mode) => {
                    this.setState({ productDisplayMode: mode });
                    AsyncStorage.setItem('productDisplayMode', mode);
                },
                setFoodSortMode: (mode) => {
                    this.setState({ foodSortMode: mode });
                    AsyncStorage.setItem('foodSortMode', mode);
                },
                setProductColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ productColumnCount: numCount });
                    AsyncStorage.setItem('productColumnCount', String(numCount));
                },
                setCategoryColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ categoryColumnCount: numCount });
                    AsyncStorage.setItem('categoryColumnCount', String(numCount));
                },
                setEventColumnCount: (count) => {
                    const numCount = Number(count);
                    this.setState({ eventColumnCount: numCount });
                    AsyncStorage.setItem('eventColumnCount', String(numCount));
                },
            };
        }

        if (this._contextValueState !== this.state) {
            this._contextValueState = this.state;
            this._contextValue = { ...this.state, ...this._contextActions };
        }

        return this._contextValue;
    };

    render() {
        const { children } = this.props;
        return (
            <SystemContext.Provider value={this._getContextValue()}>
                {children}
            </SystemContext.Provider>
        );
    }
}

export default React.memo(SystemProvider);
